import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isNull, omitBy } from 'lodash';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'react-i18next';
import Head from 'next/head';

import useGeolocation, { useGeolocationData } from 'lib/hooks/useGeoLocation';
import { SearchV2ApiLocationParams } from 'types/Apis/SearchV2LocationParams';
import useBuildVdpPageProps from 'lib/hooks/useBuildVdpPageProps';
import { useShowVdp } from 'lib/hooks/useShowVdp';
import getUtmParams from 'lib/getUtmParams';
import WindowLocation from 'lib/WindowLocation';

import { utmTrackingReceived } from 'redux-implementations/utmTracking';

import Layout, { LayoutProps } from 'components/Layout';
import Srp from 'components/Srp';
import GeoIP from 'components/tags/GeoIP';

import { HeaderContainerWidth } from 'components/Header';
import { AdGlobalRefreshProvider } from 'contexts/AdGlobalRefreshContext';
import SrpEventContext from 'contexts/SrpEventContext';
import { SourcePages } from 'lib/EventSchema';
import { setGeolocationData } from 'redux-implementations/geolocation';
import VdpOrNotFound from './noindex/listings/[vin]';

export default function SrpPage(): JSX.Element {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  /* 
    Tracks whether the geolocation-based useEffect has run, prevents useGeolocation from making a
    preemptive search before the window object has loaded and sessionStorage has been accessible 
  */
  const [initialGeolocationLoaded, setInitialGeolocationLoaded] = useState(false);
  const [shouldSkipGeolocate, setShouldSkipGeolocate] = useState<boolean | undefined>(undefined);
  let existingGeolocationData = useGeolocationData();
  console.log('GEOLOCATION - existing data', existingGeolocationData);
  useEffect(() => {
    /* 
      Attempt to retrieve geolocation from sessionStorage that would have been set 
      from a successful MaxMind call. If geolocation state doesn't exist in Redux,
      the geolocation sessionStorage data is set and them removed from sessionStorage
    */
    const sessionStorageGeolocation = JSON.parse(sessionStorage.getItem('geolocation') as string);
    console.log('GEOLOCATION - SESSION', sessionStorageGeolocation);
    if (!existingGeolocationData && sessionStorageGeolocation) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      existingGeolocationData = sessionStorageGeolocation;
      dispatch(setGeolocationData(sessionStorageGeolocation));
    }
    setInitialGeolocationLoaded(true);
  }, []);

  useEffect(() => {
    if (initialGeolocationLoaded) {
      setShouldSkipGeolocate(!!existingGeolocationData?.city);
    }
  }, [initialGeolocationLoaded, existingGeolocationData]);
  console.log('GEOLOCATION - should skip', shouldSkipGeolocate);
  const { geolocation } = useGeolocation(shouldSkipGeolocate);
  // Geolocation data will either be coming from Redux/sessionStorage or a new geolocate call
  const usedGeolocation = geolocation || existingGeolocationData;

  const locationDetails: SearchV2ApiLocationParams = {
    city: usedGeolocation?.city,
    state: usedGeolocation?.stateAbbreviation,
    latitude: usedGeolocation?.latitude,
    longitude: usedGeolocation?.longitude,
    zip: usedGeolocation?.zip,
  };

  const props = useBuildVdpPageProps();
  const [showVdp, { vehicleNotFound }] = useShowVdp(props);

  const getVdpLayoutProps = (): LayoutProps => ({
    title: props.vehicle
      ? `${props.vehicle.year} ${props.vehicle.make} ${props.vehicle.model}`
      : t(`main.meta.pageTitle`),
    description: '',
    includeAds: true,
    indexed: false,
    footer: <div />,
    showHeader: false,
  });

  const getSrpLayoutProps = (): LayoutProps => ({
    title: t(`main.meta.pageTitle`),
    description: t(`main.meta.metaDescription`),
    headerContainerWidth: 'lg' as HeaderContainerWidth,
    indexed: false,
  });

  const layoutProps = showVdp ? getVdpLayoutProps() : getSrpLayoutProps();

  useEffect(() => {
    const utmParams = getUtmParams(WindowLocation.search);
    dispatch(utmTrackingReceived(omitBy(utmParams, isNull)));
  });

  return (
    <div>
      <div id="srp-wrapper">
        <AdGlobalRefreshProvider>
          <Layout {...layoutProps}>
            <Head>
              <link
                rel="preload"
                as="image"
                href="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODE3IiBoZWlnaHQ9IjQ4NyIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2ZXJzaW9uPSIxLjEiLz4="
              />
              <GeoIP />
            </Head>
            {showVdp ? (
              <VdpOrNotFound {...props} />
            ) : (
              <SrpEventContext.Provider
                value={{ sourcePage: SourcePages.Srp, sourcePageCategory: SourcePages.Listings }}
              >
                <Srp initialSearchParams={locationDetails} vehicleNotFound={vehicleNotFound} />
              </SrpEventContext.Provider>
            )}
          </Layout>
        </AdGlobalRefreshProvider>
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ res }) => {
  res.setHeader('Cache-Control', 'public, max-age=900, must-revalidate');

  return {
    props: {},
  };
};
